import React, { useEffect, useRef, useState } from 'react'

export default function ShopDesignsPicker({
  artworks = [],
  loading = false,
  error = null,
  importedIds = new Set(),
  atImageLimit = false,
  onImport,
  compact = false,
  idPrefix = 'shop-designs',
  // Search and paging from useShopDesigns; omitted, the picker shows the list it is given.
  search = null,
}) {
  const [importingId, setImportingId] = useState(null)
  const [importError, setImportError] = useState(null)
  const displayedError = importError || error
  const activeQuery = search?.activeQuery || ''
  const searchInputId = `${idPrefix}-search`
  const categories = search?.categories || []
  const categorySelectId = `${idPrefix}-category`
  const selectedCategory = categories.find(entry => entry.publicId === search?.category) || null
  const categoryNotice = search?.categoryNotice || null
  // The search box hides itself for a small catalog; categories are worth showing regardless.
  const showControls = Boolean(search?.searchable || categories.length > 0)
  const gridRef = useRef(null)
  // Where the list ended when Load more was pressed, so focus can follow the new designs.
  const appendedFromRef = useRef(null)
  // One always-mounted status region so assistive tech announces text changes rather than
  // element mount/unmount, and the loading line and notice never render at the same time.
  const statusText = categoryNotice
    || (loading ? 'Loading shop designs…' : null)
    || (!loading && activeQuery && artworks.length > 0
      ? `${search.total} ${search.total === 1 ? 'design matches' : 'designs match'} “${activeQuery}”${selectedCategory ? ` in ${selectedCategory.name}` : ''}.`
      : null)
    || (!loading && !activeQuery && selectedCategory && artworks.length > 0
      ? `${search.total} ${search.total === 1 ? 'design' : 'designs'} in ${selectedCategory.name}.`
      : null)
    || ''

  // Load more unmounts once the last page arrives, which would drop keyboard focus to the page.
  useEffect(() => {
    const appendedFrom = appendedFromRef.current
    if (appendedFrom == null || artworks.length <= appendedFrom) return

    appendedFromRef.current = null
    const newCards = [...(gridRef.current?.children || [])].slice(appendedFrom)
    newCards.map(card => card.querySelector('button:not(:disabled)')).find(Boolean)?.focus()
  }, [artworks.length])

  const handleLoadMore = () => {
    if (search.loadingMore) return

    appendedFromRef.current = artworks.length
    search.onLoadMore()
  }

  const handleImport = async publicId => {
    if (!onImport || atImageLimit) return

    setImportingId(publicId)
    setImportError(null)
    try {
      await onImport(publicId)
    } catch (importFailure) {
      setImportError(importFailure.message || 'Could not add the shop design')
    } finally {
      setImportingId(null)
    }
  }

  return (
    <section
      className={`guided-setup__shop-designs${compact ? ' shop-designs-picker--compact' : ''}`}
      aria-labelledby={`${idPrefix}-title`}
    >
      <div className="guided-setup__shop-designs-heading">
        <div>
          <h3 id={`${idPrefix}-title`}>Shop designs</h3>
          <p>Add a reusable design provided by this shop. Preview images are watermarked.</p>
        </div>
      </div>

      {showControls && (
        <div
          className={`guided-setup__shop-designs-search${search.searchable && categories.length > 0 ? ' guided-setup__shop-designs-search--two-up' : ''}`}
          role="search"
        >
          {search.searchable && (
            <div className="guided-setup__shop-designs-field">
              <label htmlFor={searchInputId}>Search shop designs</label>
              <input
                id={searchInputId}
                type="search"
                value={search.query}
                maxLength={100}
                placeholder="Design name"
                autoComplete="off"
                onChange={event => search.onQueryChange(event.target.value)}
              />
            </div>
          )}
          {categories.length > 0 && (
            <div className="guided-setup__shop-designs-field">
              <label htmlFor={categorySelectId}>Category</label>
              <select
                id={categorySelectId}
                value={search.category || ''}
                onChange={event => search.onCategoryChange(event.target.value)}
              >
                <option value="">All categories</option>
                {categories.map(entry => (
                  <option key={entry.publicId} value={entry.publicId}>
                    {entry.name} ({entry.count})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      <p className="guided-setup__shop-designs-status" role="status">{statusText}</p>
      {displayedError && (
        <p className="guided-setup__shop-designs-status guided-setup__shop-designs-status--error" role="alert">
          {displayedError}
        </p>
      )}
      {!loading && !displayedError && artworks.length === 0 && (
        <p className="guided-setup__shop-designs-status">
          {activeQuery
            ? selectedCategory
              ? `No designs in ${selectedCategory.name} match “${activeQuery}”.`
              : `No shop designs match “${activeQuery}”.`
            : selectedCategory
              ? 'No designs in this category.'
              : 'This shop has not added any reusable designs yet.'}
        </p>
      )}
      {artworks.length > 0 && (
        <ul className="guided-setup__shop-design-grid" ref={gridRef}>
          {artworks.map(artwork => {
            const imported = importedIds.has(String(artwork.publicId))
            const importing = String(importingId) === String(artwork.publicId)
            return (
              <li className="guided-setup__shop-design-card" key={artwork.publicId}>
                <img
                  src={artwork.previewUrl}
                  alt={`Watermarked preview of ${artwork.title}`}
                  loading="lazy"
                />
                <div>
                  <strong>{artwork.title}</strong>
                  {artwork.widthPx && artwork.heightPx && (
                    <span>{artwork.widthPx} × {artwork.heightPx} px</span>
                  )}
                </div>
                <button
                  className="gs-btn-secondary"
                  type="button"
                  disabled={atImageLimit || imported || importing}
                  aria-label={imported
                    ? `${artwork.title} is already added`
                    : `Add ${artwork.title} to this gang sheet`}
                  onClick={() => handleImport(artwork.publicId)}
                >
                  {imported ? 'Added' : importing ? 'Adding…' : 'Add design'}
                </button>
              </li>
            )
          })}
        </ul>
      )}
      {search?.hasMore && !loading && artworks.length > 0 && (
        <button
          className="gs-btn-secondary guided-setup__shop-designs-more"
          type="button"
          // aria-disabled keeps keyboard focus on the button while the next page loads.
          aria-disabled={search.loadingMore}
          onClick={handleLoadMore}
        >
          {search.loadingMore ? 'Loading…' : 'Load more designs'}
        </button>
      )}
    </section>
  )
}
